import React, { memo, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import isValid from 'date-fns/isValid';
import css from 'styled-jsx/css';
import Tooltip from 'react-tooltip';

import { COMPONENT_OVERRIDE } from '../../Styles/Z-INDEX';

export default memo(ImageOverlayWidget);

const { className: tooltipClassName, styles: tooltipStyles } = css.resolve`
  div {
    z-index: ${COMPONENT_OVERRIDE};
  }
`;

function ImageOverlayWidget(props) {
  const dataMap = Array.isArray(props.data)
    ? new Map(
        props.data.map(({ data: [signalValue] = [], name }) => [
          name,
          signalValue,
        ])
      )
    : new Map();

  const [signalOverlays, setSignalOverlays] = useState(new Map());
  const imgRef = useRef();
  const resizeObserverRef = useRef();

  const disconnectResizeObserver = () => {
    resizeObserverRef?.current?.disconnect();
  };

  const generateSignalOverlays = () => {
    if (!imgRef?.current?.getBoundingClientRect) return;

    const { height, width } = imgRef.current.getBoundingClientRect() || {};
    const container = document.getElementById(props.widget?._id);
    const { height: containerHeight = 0, width: containerWidth = 0 } =
      container?.getBoundingClientRect?.() || {};

    const offsetX = (containerWidth - width) / 2;
    const offsetY = (containerHeight - height) / 2;

    const signalOverlayMap = new Map(
      props.widget?.settings?.lines?.map?.(({ datapointID, key, x, y }) => {
        const clientX = width * (x / 100) + offsetX;
        const clientY = height * (y / 100) + offsetY;

        const [{ createdAt } = {}] =
          props.fetchedData?.get?.(datapointID) || [];
        const createdAtDate = new Date(createdAt);
        const timestamp = isValid(createdAtDate)
          ? format(createdAtDate, 'MM-dd-yyyy HH:mm:ss')
          : '';

        const { display_precision, unit } =
          props.signalUnitsMap?.get?.(datapointID)?.[key] || {};

        const value = formatDisplayValue({
          display_precision,
          unit,
          value: dataMap.get(key),
        });
        return [`${clientX}-${clientY}`, { key, timestamp, value }];
      }) || []
    );
    setSignalOverlays(signalOverlayMap);
  };

  const handleTooltipHide = ({ target: { id } }) => {
    const coordinates = id.substring(0, id.lastIndexOf('-'));
    [...signalOverlays.keys()].forEach((c) => {
      if (c === coordinates) return;

      const overlayElement = document.getElementById(`${c}-overlay`);
      const className = overlayElement.className
        .replace('invisible', '')
        .trim();
      overlayElement.className = className;
    });
  };

  const handleTooltipShow = ({ target: { id } }) => {
    const coordinates = id.substring(0, id.lastIndexOf('-'));
    [...signalOverlays.keys()].forEach((c) => {
      if (c === coordinates) return;

      const overlayElement = document.getElementById(`${c}-overlay`);
      const className = overlayElement.className.concat(' invisible');
      overlayElement.className = className;
    });
  };

  const initResizeObserver = () => {
    if (!window.ResizeObserver) return;

    const container = document.getElementById(props.widget?._id);
    resizeObserverRef.current = new window.ResizeObserver(() => {
      generateSignalOverlays();
    });
    resizeObserverRef.current.observe(container);
  };

  useEffect(() => {
    if (!signalOverlays.size) return;

    generateSignalOverlays();
  }, [...(dataMap?.values?.() || [])]);

  useEffect(() => {
    if (!resizeObserverRef?.current) initResizeObserver();
    return disconnectResizeObserver;
  }, []);

  return (
    <div className="h-100 w-100 d-flex align-items-center justify-content-center">
      <img
        alt="annotated overlay signals"
        className="img-fluid mx-auto mh-100 rounded rounded-lg"
        onLoad={generateSignalOverlays}
        ref={imgRef}
        src={props.widget?.settings?.image}
      />
      {signalOverlays.size
        ? [...signalOverlays.entries()].map(([coordinates, config]) => {
            const [x, y] = coordinates.split('-');
            const { key, timestamp, value } = config || {};

            const {
              className: signalOverlayClassName,
              styles: signalOverlayStyles,
            } = getOverlayMarkerStyle(x, y);
            return (
              <span
                className={`py-1 px-2 position-fixed rounded rounded-lg shadow-lg text-white three-quarter-text signal-overlay ${signalOverlayClassName}`}
                data-for={coordinates}
                data-tip
                id={`${coordinates}-overlay`}
                key={coordinates}
              >
                {!value && value !== 0 ? 'No data' : value}
                <Tooltip
                  afterHide={handleTooltipHide}
                  afterShow={handleTooltipShow}
                  backgroundColor="var(--brand-primary)"
                  border
                  className={tooltipClassName}
                  effect="solid"
                  id={coordinates}
                >
                  <p className="mb-0 text-white text-center">{key}</p>
                  {timestamp ? (
                    <p className="mb-0 text-white text-center">{timestamp}</p>
                  ) : null}
                  {tooltipStyles}
                </Tooltip>
                {signalOverlayStyles}
              </span>
            );
          })
        : null}
    </div>
  );
}

ImageOverlayWidget.propTypes = {
  asset: PropTypes.object,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.array,
      name: PropTypes.string,
    })
  ),
  fetchedData: PropTypes.object,
  signalUnitsMap: PropTypes.object,
  widget: PropTypes.shape({
    _id: PropTypes.string,
    settings: PropTypes.shape({
      image: PropTypes.string,
      lines: PropTypes.arrayOf(
        PropTypes.shape({
          datapointID: PropTypes.string,
          key: PropTypes.string,
          x: PropTypes.number,
          y: PropTypes.number,
        })
      ),
    }),
  }),
};

function formatDisplayValue({ display_precision = 2, unit, value }) {
  const displayUnit = unit ? ` ${unit}` : '';
  switch (true) {
    case !value && value !== 0:
      return value;
    case typeof value === 'string':
    case Number.isInteger(value):
      return `${value}${displayUnit}`;
    case Number.isFinite(value):
      return `${value.toFixed(display_precision)}${displayUnit}`;
    default:
      return value;
  }
}

function getOverlayMarkerStyle(x, y) {
  return css.resolve`
    .signal-overlay {
      background-color: var(--dark);
      left: ${x}px;
      opacity: 0.9;
      top: ${y}px;
    }
  `;
}
